import {
  Box,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useTheme,
  Grid,
  Icon,
  alpha,
} from "@mui/material";
import dayjs from "dayjs";
import React from "react";
import { formatCurrency } from "../../utils/formatters";
import { DaybookSession, SummaryPanelProps } from "./types";
import { getPanelStyles } from "./styles";

const LoadingSkeleton: React.FC = () => (
  <Stack spacing={2}>
    <Skeleton variant="rounded" height={100} />
    <Skeleton variant="rounded" height={150} />
    <Skeleton variant="rounded" height={200} />
  </Stack>
);

const DetailBox: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 1,
        border: "1px solid",
        borderColor: alpha(theme.palette.divider, 0.8),
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        backgroundColor: alpha(theme.palette.background.default, 0.5),
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    </Paper>
  );
};

const SessionDetails: React.FC<{ session: DaybookSession }> = ({ session }) => {
  return (
    <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
      <Box sx={{ flex: 1 }}>
        <DetailBox 
          label="Cashier" 
          value={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Icon sx={{ fontSize: 16, color: '#1E8078' }}>person</Icon>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#1E8078' }}>
                {session.cashier?.name || "-"}
              </Typography>
            </Box>
          } 
        />
      </Box>
      <Box sx={{ flex: 1 }}>
        <DetailBox 
          label="Branch" 
          value={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Icon sx={{ fontSize: 16, color: '#1E8078' }}>store</Icon>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#1E8078' }}>
                {session.business?.branch_name || "-"}
              </Typography>
            </Box>
          }
        />
      </Box>
      <Box sx={{ flex: 1 }}>
        <DetailBox
          label="Status"
          value={
            <Chip
              label={session.status || "-"}
              color={session.status === "active" ? "success" : "default"}
              size="small"
              variant="outlined"
              sx={{
                height: 24,
                textTransform: "capitalize",
                fontWeight: 500,
                '&.MuiChip-colorSuccess': {
                  color: '#1E8078',
                  borderColor: alpha('#1E8078', 0.3),
                  backgroundColor: alpha('#1E8078', 0.05),
                }
              }}
            />
          }
        />
      </Box>
      <Box sx={{ flex: 1 }}>
        <DetailBox
          label="Duration"
          value={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Icon sx={{ fontSize: 16, color: '#1E8078' }}>schedule</Icon>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#1E8078' }}>
                {(() => {
                  const start = dayjs(session.timestamps.start);
                  const end = session.timestamps.end
                    ? dayjs(session.timestamps.end)
                    : dayjs();
                  return `${end.diff(start, "hour")}h ${
                    end.diff(start, "minute") % 60
                  }m`;
                })()}
              </Typography>
            </Box>
          }
        />
      </Box>
    </Stack>
  );
};

const AmountsSummary: React.FC<{ session: DaybookSession }> = ({ session }) => {
  const theme = useTheme();

  return (
    <Paper 
      elevation={0} 
      sx={{
        p: 2,
        borderRadius: 1,
        border: "1px solid",
        borderColor: alpha(theme.palette.divider, 0.8),
        backgroundColor: alpha(theme.palette.background.default, 0.5),
      }}
    >
      <Typography 
        variant="subtitle2" 
        sx={{ 
          fontWeight: 600, 
          mb: 2,
          color: '#1E8078',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5
        }}
      >
        <Icon sx={{ fontSize: 20 }}>account_balance_wallet</Icon>
        Amounts Summary
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Initial Amount
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#1E8078' }}>
              {formatCurrency(session.amounts.initial)}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={3}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Final Amount
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#1E8078' }}>
              {formatCurrency(session.amounts.final)}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={3}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Difference
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: session.amounts.difference >= 0 ? '#1E8078' : theme.palette.error.main,
              }}
            >
              {formatCurrency(session.amounts.difference)}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={3}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Total Sales
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#1E8078' }}>
              {formatCurrency(session.amounts.total_sales)}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

const DenominationsList: React.FC<{
  title: string;
  denominations: DaybookSession["denominations"]["initial" | "final"];
  isActive?: boolean;
}> = ({ title, denominations, isActive }) => {
  const theme = useTheme();
  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Typography 
        variant="subtitle2" 
        sx={{ 
          mb: 2, 
          fontWeight: 600,
          color: '#1E8078',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5
        }}
      >
        <Icon sx={{ fontSize: 20 }}>
          {title === "Initial Count" ? "start" : "done_all"}
        </Icon>
        {title}
      </Typography>
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        {isActive && title === "Final Count" ? (
          <Box
            sx={{
              flex: 1,
              border: "2px dashed",
              borderColor: alpha(theme.palette.divider, 0.8),
              borderRadius: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              backgroundColor: alpha(theme.palette.background.default, 0.5),
              p: 2,
            }}
          >
            <Icon sx={{ fontSize: 32, color: '#1E8078' }}>pending</Icon>
            <Typography variant="body2" sx={{ color: '#1E8078', fontWeight: 500 }} align="center">
              Session is still active
            </Typography>
            <Typography variant="caption" color="text.secondary" align="center">
              Final count will be available when session ends
            </Typography>
          </Box>
        ) : (
          <Box sx={{ flex: 1 }}>
            {denominations.bills.map((bill) => (
              <Box
                key={bill.value}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {bill.value} BHD
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#1E8078' }}>
                  {bill.count}
                </Typography>
              </Box>
            ))}
            {denominations.coins.map((coin) => (
              <Box
                key={coin.value}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {coin.value} BHD
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#1E8078' }}>
                  {coin.count}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const DenominationsSummary: React.FC<{ session: DaybookSession }> = ({
  session,
}) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 1,
        border: "1px solid",
        borderColor: alpha(theme.palette.divider, 0.8),
        backgroundColor: alpha(theme.palette.background.default, 0.5),
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <DenominationsList
            title="Initial Count"
            denominations={session.denominations.initial}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <DenominationsList
            title="Final Count"
            denominations={session.denominations.final}
            isActive={session.status === "active"}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export const DayBookSummaryPanel: React.FC<SummaryPanelProps> = ({
  loading,
  sessions,
  selectedSession,
}) => {
  const theme = useTheme();
  const styles = getPanelStyles(theme);

  if (loading) return <LoadingSkeleton />;

  const session = sessions.find((s) => s.id === selectedSession);
  if (!session) return null;

  return (
    <Box sx={{ ...styles.container, p: 2 }}>
      <Stack spacing={2}>
        <SessionDetails session={session} />
        <AmountsSummary session={session} />
        <DenominationsSummary session={session} />
      </Stack>
    </Box>
  );
};
